export type Candidate = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  kind: "person" | "agent";
  here: boolean;

  role?: string;
};

export type Asking = { at: number; fragment: string };

const OF_A_HANDLE = /^[A-Za-z0-9-]$/;

function opensOne(text: string, at: number): boolean {
  if (at === 0) return true;
  const before = text[at - 1] ?? "";
  return /\s/.test(before);
}

export function whatIsBeingTyped(text: string, caret: number): Asking | null {
  const upTo = Math.max(0, Math.min(caret, text.length));
  for (let i = upTo - 1; i >= 0; i--) {
    const ch = text[i] ?? "";
    if (ch === "@") {
      if (!opensOne(text, i)) return null;
      return { at: i, fragment: text.slice(i + 1, upTo) };
    }
    if (!OF_A_HANDLE.test(ch)) return null;
  }
  return null;
}

export const AT_MOST = 6;

const lower = (s: string): string => s.toLowerCase();

export function matching(all: readonly Candidate[], fragment: string): Candidate[] {
  const want = lower(fragment.trim());
  const rank = (one: Candidate): number => {
    const handle = lower(one.handle);
    const name = lower(one.displayName ?? "");
    if (want === "") return 0;
    if (handle === want) return 0;
    if (handle.startsWith(want)) return 1;
    if (name.startsWith(want)) return 2;
    if (handle.includes(want) || name.includes(want)) return 3;
    return -1;
  };
  return all
    .map((one, order) => ({ one, order, rank: rank(one) }))
    .filter((row) => row.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, AT_MOST)
    .map((row) => row.one);
}

export function withMention(
  text: string,
  asking: Asking,
  handle: string,
  caret: number,
): { text: string; caret: number } {
  const upTo = Math.max(asking.at, Math.min(caret, text.length));
  const written = `@${handle} `;
  return {
    text: text.slice(0, asking.at) + written + text.slice(upTo),
    caret: asking.at + written.length,
  };
}

export function spellsMention(text: string, handle: string): boolean {
  return lower(text).includes(`@${lower(handle)}`);
}

export function alsoMeant(chosen: string, all: readonly Candidate[]): Candidate[] {
  return all.filter((one) => one.handle !== chosen && lower(chosen).startsWith(lower(one.handle)));
}

export type Present = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  role?: string;
};

export type Usable = { account: string; handle: string; avatar: string | null };

export function namesIn(people: readonly Present[], agents: readonly Usable[]): Candidate[] {
  const isAnAgent = new Set(agents.map((one) => one.account));
  return people.map((one) => ({
    account: one.account,
    handle: one.handle,
    displayName: one.displayName,
    avatar: one.avatar,
    kind: isAnAgent.has(one.account) ? "agent" : "person",
    here: true,
    role: one.role,
  }));
}

export function offeredIn(people: readonly Present[], agents: readonly Usable[], me: string): Candidate[] {
  const here = new Set(people.map((one) => one.account));
  return [
    ...namesIn(people, agents).filter((one) => one.account !== me),
    ...agents
      .filter((one) => !here.has(one.account))
      .map((one) => ({
        account: one.account,
        handle: one.handle,
        displayName: null,
        avatar: one.avatar ?? null,
        kind: "agent" as const,
        here: false,
      })),
  ];
}

export type Run = { at: number; text: string; mentions: Candidate | null };

export function runsOf(text: string, all: readonly Candidate[]): Run[] {
  const known = [...all].sort((a, b) => b.handle.length - a.handle.length);
  const runs: Run[] = [];
  let plain = "";
  let from = 0;
  let i = 0;
  while (i < text.length) {
    const found =
      text[i] === "@" && opensOne(text, i)
        ? known.find((one) => lower(text).startsWith(`@${lower(one.handle)}`, i))
        : undefined;

    const after = found ? (text[i + 1 + found.handle.length] ?? "") : "";
    if (found && !OF_A_HANDLE.test(after)) {
      if (plain) runs.push({ at: from, text: plain, mentions: null });
      plain = "";
      runs.push({ at: i, text: text.slice(i, i + 1 + found.handle.length), mentions: found });
      i += 1 + found.handle.length;
      from = i;
      continue;
    }
    plain += text[i];
    i += 1;
  }
  if (plain) runs.push({ at: from, text: plain, mentions: null });
  return runs;
}
